<div data-spartan-page class="p-6 md:p-8 space-y-6"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Loading -->
  @if (isLoading) {
    <div class="space-y-4">
      <app-skeleton [rows]="3" rowHeight="h-8" />
      <app-skeleton [rows]="8" />
    </div>

  <!-- Error (no order) -->
  } @else if (errorMessage && !order) {
    <app-inline-alert tone="danger" [message]="errorMessage" />

  <!-- Detail -->
  } @else if (order) {

    <!-- Page header -->
    <app-page-header
      eyebrow="Shopee / Orders"
      [title]="order.shopeeOrderNo"
      [subtitle]="order.uniqueId"
    >
      <div actions class="flex flex-wrap items-center gap-2">
        <app-status-badge
          [variant]="getOrderStatusVariant(order.status)"
          [label]="order.status"
        />
        <button hlmBtn variant="outline" size="sm"
                [disabled]="!canPerformRepack()"
                (click)="initiateRepackCreate()">
          <ng-icon hlm name="lucidePlusCircle" class="mr-1.5 size-3.5" />
          Create Repack
        </button>
        <button hlmBtn variant="outline" size="sm"
                [disabled]="!canPerformRepack()"
                (click)="initiateRepackUpdate()">
          <ng-icon hlm name="lucidePencil" class="mr-1.5 size-3.5" />
          Update Repack
        </button>
        <button hlmBtn variant="destructive" size="sm"
                [disabled]="!canPerformRepack()"
                (click)="initiateRepackCancel()">
          <ng-icon hlm name="lucideXCircle" class="mr-1.5 size-3.5" />
          Cancel Repack
        </button>
        <button hlmBtn variant="outline" size="sm"
                [disabled]="!order?.parcels || order.parcels.length === 0"
                (click)="openCustomsClearanceModal()">
          <ng-icon hlm name="lucideClipboardCheck" class="mr-1.5 size-3.5" />
          Cust. Clearance
        </button>
      </div>
    </app-page-header>

    <!-- Inline alerts -->
    @if (successMessage) {
      <app-inline-alert tone="success" [message]="successMessage" />
    }
    @if (errorMessage) {
      <app-inline-alert tone="danger" [message]="errorMessage" />
    }
    @if (!canPerformRepack()) {
      <app-inline-alert
        tone="warning"
        message="Repack operations unavailable: This carton has been cancelled or is missing critical information."
      />
    }

    <!-- Two-column detail grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

      <!-- Order Information -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Order Information</h2>
        </div>
        <div class="p-4">
          <dl class="divide-y divide-border">
            @for (row of [
              { label: 'Unique ID',         value: order.uniqueId },
              { label: 'Shopee Order No',   value: order.shopeeOrderNo },
              { label: 'Carrier TN',        value: order.carrierTrackingNumber || '—' },
              { label: 'Carton No',         value: order.cartonNo || '—' },
              { label: 'Destination',       value: order.destinationRegionName || order.destinationRegion || '—' },
              { label: 'Goods Type',        value: order.goodsType || '—' },
              { label: 'Service Code',      value: order.serviceCode || '—' },
              { label: 'Transport Type',    value: order.transportType || '—' }
            ]; track row.label) {
              <div class="py-2 flex justify-between gap-4 text-sm">
                <dt class="font-medium shrink-0 text-muted-foreground">{{ row.label }}</dt>
                <dd class="text-right font-mono text-[12.5px] text-foreground">{{ row.value }}</dd>
              </div>
            }
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Created</dt>
              <dd class="text-right tabular-nums text-xs text-foreground">{{ order.createdAt | date:'yyyy-MM-dd HH:mm:ss' }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Last Updated</dt>
              <dd class="text-right tabular-nums text-xs text-foreground">{{ order.lastUpdated | date:'yyyy-MM-dd HH:mm:ss' }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Carton Dimensions & Weight -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Carton Dimensions &amp; Weight</h2>
        </div>
        <div class="p-4">
          <dl class="divide-y divide-border">
            @for (row of [
              { label: 'Weight',  value: order.cartonWeight  != null ? (order.cartonWeight + ' kg')  : '—' },
              { label: 'Length',  value: order.cartonLength  != null ? (order.cartonLength + ' cm')  : '—' },
              { label: 'Width',   value: order.cartonWidth   != null ? (order.cartonWidth  + ' cm')  : '—' },
              { label: 'Height',  value: order.cartonHeight  != null ? (order.cartonHeight + ' cm')  : '—' },
              { label: 'Volume',  value: order.cartonVolume  != null ? (order.cartonVolume + ' m³')  : '—' }
            ]; track row.label) {
              <div class="py-2 flex justify-between gap-4 text-sm">
                <dt class="font-medium shrink-0 text-muted-foreground">{{ row.label }}</dt>
                <dd class="text-right tabular-nums font-mono text-[12.5px] text-foreground">{{ row.value }}</dd>
              </div>
            }
          </dl>
        </div>
      </div>
    </div>

    <!-- Parcels table -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 class="text-sm font-semibold text-foreground">
          Parcels
          <span class="ml-1.5 tabular-nums font-normal text-xs text-muted-foreground">
            ({{ order.parcels?.length || 0 }})
          </span>
        </h2>
      </div>

      @if (order.parcels && order.parcels.length > 0) {
        <div class="overflow-x-auto">
          <table class="w-full text-sm" aria-label="Parcels">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                <th class="text-left px-4 py-2.5 font-medium">Tracking #</th>
                <th class="text-right px-4 py-2.5 font-medium">Weight</th>
                <th class="text-left px-4 py-2.5 font-medium">Dimensions (L×W×H)</th>
                <th class="text-left px-4 py-2.5 font-medium">Receiver Name</th>
                <th class="text-left px-4 py-2.5 font-medium">Receiver Address</th>
              </tr>
            </thead>
            <tbody>
              @for (parcel of order.parcels; track parcel.trackingNumber) {
                <tr class="h-12 border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                  <td class="px-4 font-mono text-[12px] font-medium">{{ parcel.trackingNumber }}</td>
                  <td class="px-4 text-right tabular-nums text-xs text-muted-foreground">
                    {{ parcel.weight != null ? (parcel.weight + ' kg') : '—' }}
                  </td>
                  <td class="px-4 font-mono text-[12px] text-muted-foreground">
                    {{ parcel.length || '—' }} × {{ parcel.width || '—' }} × {{ parcel.height || '—' }} cm
                  </td>
                  <td class="px-4 text-sm">
                    {{ parcel.Receiver_info?.receiver_name || '—' }}
                  </td>
                  <td class="px-4 text-xs max-w-[250px] text-muted-foreground">
                    @if (parcel.Receiver_info?.receiver_address) {
                      <span class="line-clamp-1">
                        {{ parcel.Receiver_info?.receiver_address }}{{ parcel.Receiver_info?.receiver_city ? ', ' + parcel.Receiver_info?.receiver_city : '' }}{{ parcel.Receiver_info?.receiver_state ? ', ' + parcel.Receiver_info?.receiver_state : '' }}
                      </span>
                    } @else {
                      —
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <app-empty-state
          icon="lucidePackage"
          heading="No parcels"
          description="No parcels associated with this order."
        />
      }
    </div>

  } @else if (!isLoading && !errorMessage) {
    <app-empty-state
      icon="lucideFileText"
      heading="Order not found"
      description="The requested order could not be loaded."
    />
  }
</div>


<!-- ═══════════════════════════════════════════════════════
     MODALS — no Bootstrap classes changed; logic preserved
     ═══════════════════════════════════════════════════════ -->

<!-- Repack Create Confirmation Modal -->
<ng-template #repackCreateConfirmModal let-modal>
  <div class="modal-header bg-warning">
    <h5 class="modal-title">
      <i class="bi bi-exclamation-triangle me-2"></i>
      Confirm Repack Creation
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="alert alert-warning">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <strong>Warning:</strong> This action will cancel the current carton.
    </div>
    <p>You are about to cancel carton <strong>{{order?.cartonNo || order?.carrierTrackingNumber}}</strong> and create a new carton with its parcels.</p>
    <p>All parcels from this carton will be unbinded first, then assigned to a new carton.</p>
    <p class="mb-0"><strong>Are you sure you want to proceed?</strong></p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
      <i class="bi bi-x-circle me-1"></i>
      Cancel
    </button>
    <button type="button" class="btn btn-warning" (click)="modal.close('confirm')">
      <i class="bi bi-check-circle me-1"></i>
      Yes, Cancel and Create New
    </button>
  </div>
</ng-template>

<!-- Repack Update Options Modal -->
<ng-template #repackUpdateOptionsModal let-modal>
  <div class="modal-header bg-primary text-white">
    <h5 class="modal-title">
      <i class="bi bi-box-seam me-2"></i>
      Repack Update Options
    </h5>
    <button type="button" class="btn-close btn-close-white" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <p>What would you like to do with carton <strong>{{order?.cartonNo || order?.carrierTrackingNumber}}</strong>?</p>

    <div class="d-grid gap-3 mt-4">
      <button type="button" class="btn btn-outline-primary btn-lg text-start" (click)="modal.close('add')">
        <div class="d-flex align-items-center">
          <div class="me-3"><i class="bi bi-plus-circle-fill fs-2"></i></div>
          <div>
            <strong>Add Parcels</strong>
            <div class="small text-muted">Add parcels from cancelled cartons to this carton</div>
          </div>
        </div>
      </button>
      <button type="button" class="btn btn-outline-primary btn-lg text-start" (click)="modal.close('remove')">
        <div class="d-flex align-items-center">
          <div class="me-3"><i class="bi bi-dash-circle-fill fs-2"></i></div>
          <div>
            <strong>Remove Parcels</strong>
            <div class="small text-muted">Remove parcels from this carton for rebinding elsewhere</div>
          </div>
        </div>
      </button>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
      <i class="bi bi-x-circle me-1"></i>
      Cancel
    </button>
  </div>
</ng-template>

<!-- Repack Update Modal for parcel selection -->
<ng-template #repackUpdateModal let-modal>
  <div class="modal-header bg-warning">
    <h5 class="modal-title">
      <i class="bi bi-box me-2"></i>
      Select Parcels to Remove
    </h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="alert alert-warning">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <strong>Important:</strong> Selected parcels will be removed from this carton and must be transferred to another carton.
    </div>
    <div class="table-responsive mt-3">
      <table class="table table-sm table-hover">
        <thead>
          <tr>
            <th style="width: 50px;">
              <div class="form-check">
                <input type="checkbox" class="form-check-input"
                  [checked]="(order?.parcels?.length ?? 0) === selectedParcelsForUnbind.length && (order?.parcels?.length ?? 0) > 0"
                  (change)="onSelectAllParcelsChange($event)">
              </div>
            </th>
            <th>Tracking Number</th>
            <th>Weight</th>
            <th>Dimensions</th>
          </tr>
        </thead>
        <tbody>
          @for (parcel of order?.parcels; track parcel.trackingNumber) {
            <tr [class.table-active]="isParcelSelected(parcel)"
                style="cursor: pointer;"
                (click)="toggleParcelSelection(parcel)">
              <td>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input"
                    [checked]="isParcelSelected(parcel)"
                    (click)="$event.stopPropagation()"
                    (change)="toggleParcelSelection(parcel)">
                </div>
              </td>
              <td>{{ parcel.trackingNumber }}</td>
              <td>{{ parcel.weight || 'N/A' }} kg</td>
              <td>{{ parcel.length || 'N/A' }} × {{ parcel.width || 'N/A' }} × {{ parcel.height || 'N/A' }} cm</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    @if (selectedParcelsForUnbind.length > 0) {
      <div class="alert alert-info mt-3">
        <i class="bi bi-info-circle me-2"></i>
        <strong>{{ selectedParcelsForUnbind.length }}</strong> parcel(s) selected for unbinding.
      </div>
    }
    @if (selectedParcelsForUnbind.length === 0) {
      <div class="alert alert-danger mt-3">
        <i class="bi bi-exclamation-circle me-2"></i>
        Please select at least one parcel to proceed.
      </div>
    }
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
      <i class="bi bi-x-circle me-1"></i>
      Cancel
    </button>
    <button type="button" class="btn btn-warning"
      [disabled]="selectedParcelsForUnbind.length === 0"
      (click)="modal.close('proceed')">
      <i class="bi bi-check-circle me-1"></i>
      Unbind & Transfer to Another Carton
    </button>
  </div>
</ng-template>

<!-- Repack Cancel Modal -->
<ng-template #repackCancelModal let-modal>
  <div class="modal-header bg-danger text-white">
    <h5 class="modal-title">
      <i class="bi bi-x-circle me-2"></i>
      Cancel Repack Carton
    </h5>
    <button type="button" class="btn-close btn-close-white" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <div class="alert alert-warning">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      <strong>Warning:</strong> Cancelling this carton will release all parcels.
    </div>
    <p>You are about to cancel carton <strong>{{order?.cartonNo || order?.carrierTrackingNumber}}</strong>.</p>
    <div class="mb-3">
      <label for="reasonCode" class="form-label">Reason Code*</label>
      <select id="reasonCode" class="form-select" [(ngModel)]="repackCancelReasonCode" required>
        <option [ngValue]="1">1 - Incorrect Delivery Details</option>
        <option [ngValue]="2">2 - Damaged Carton</option>
        <option [ngValue]="3">3 - Weight/Dimension Issue</option>
        <option [ngValue]="4">4 - Service Change Required</option>
        <option [ngValue]="5">5 - Other</option>
      </select>
    </div>
    <p class="alert alert-info">
      <i class="bi bi-info-circle-fill me-2"></i>
      <strong>Note:</strong> After cancellation, you'll need to add the parcels to another existing carton.
    </p>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
      <i class="bi bi-x me-1"></i>
      Cancel
    </button>
    <button type="button" class="btn btn-danger" (click)="modal.close('confirm')">
      <i class="bi bi-check2 me-1"></i>
      Confirm Cancellation
    </button>
  </div>
</ng-template>

<!-- Customs Clearance Parcel Selection Modal (Bootstrap native) -->
<div class="modal fade" id="customsClearanceModal" tabindex="-1"
     aria-labelledby="customsClearanceModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered modal-lg">
    <div class="modal-content">
      <div class="modal-header bg-info text-white">
        <h5 class="modal-title" id="customsClearanceModalLabel">
          <i class="bi bi-clipboard-check me-2"></i>
          Select Parcels for Customs Clearance Update
        </h5>
        <button type="button" class="btn-close btn-close-white"
                data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <div class="alert alert-info">
          <i class="bi bi-info-circle-fill me-2"></i>
          <strong>Carton:</strong> {{ order?.cartonNo || order?.carrierTrackingNumber || 'N/A' }}
        </div>
        @if (!order?.parcels || order?.parcels?.length === 0) {
          <div class="alert alert-warning">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            No parcels available for customs clearance update.
          </div>
        }
        @if (order?.parcels; as parcels) {
          @if (parcels.length > 0) {
            <div class="mb-3">
              <label class="form-label">
                <i class="bi bi-search"></i> Filter Parcel Tracking Numbers
              </label>
              <input type="text" class="form-control"
                     [(ngModel)]="parcelFilterText"
                     placeholder="Type to filter tracking numbers..."
                     (ngModelChange)="filterParcels()">
            </div>
            <div class="form-check mb-3">
              <input type="checkbox" class="form-check-input" id="selectAllParcelsForCC"
                     [checked]="selectedParcelsForCC.length === filteredParcelsForCC.length && filteredParcelsForCC.length > 0"
                     (change)="toggleSelectAllParcelsForCC($event)">
              <label class="form-check-label fw-bold" for="selectAllParcelsForCC">
                Select All ({{ filteredParcelsForCC.length }} parcel{{ filteredParcelsForCC.length !== 1 ? 's' : '' }})
              </label>
            </div>
            <div class="parcel-list"
                 style="max-height: 300px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 4px; padding: 10px;">
              @for (parcel of filteredParcelsForCC; track parcel.trackingNumber) {
                <div class="form-check mb-2">
                  <input type="checkbox" class="form-check-input"
                         [id]="'parcel_cc_' + parcel.trackingNumber"
                         [checked]="isParcelSelectedForCC(parcel)"
                         (change)="toggleParcelForCC(parcel)">
                  <label class="form-check-label" [for]="'parcel_cc_' + parcel.trackingNumber">
                    {{ parcel.trackingNumber }}
                  </label>
                </div>
              }
              @if (filteredParcelsForCC.length === 0) {
                <div class="text-muted text-center py-3">
                  <i class="bi bi-search"></i> No parcels match your filter
                </div>
              }
            </div>
            <div class="alert alert-secondary mt-3 mb-0">
              <i class="bi bi-check2-square me-2"></i>
              <strong>{{ selectedParcelsForCC.length }}</strong> of <strong>{{ parcels.length }}</strong> parcel(s) selected
            </div>
          }
        }
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
          <i class="bi bi-x-circle me-1"></i>
          Cancel
        </button>
        <button type="button" class="btn btn-info"
                [disabled]="selectedParcelsForCC.length === 0"
                (click)="proceedToCustomsClearance()">
          <i class="bi bi-arrow-right-circle me-1"></i>
          Proceed to Customs Clearance ({{ selectedParcelsForCC.length }})
        </button>
      </div>
    </div>
  </div>
</div>
